import { Spacer } from '@loomhq/lens';
import classNames from 'classnames';
import _cloneDeep from 'lodash/cloneDeep';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { updateVideoPlayerBucketId } from 'actions/videoPlayer';
import { LOOM_DEV_URI, LOOM_URI } from 'constants/routes';
import { selectCommentBuckets } from 'selectors/comments';

import AddComment from './add-comment';
import Comment from './comment';

import styles from './styles.module.less';

const SCROLL_BUFFER = 140;

const CommentsContent = props => {
  const { scrollContainer, drawnOnSharePage } = props;

  const dispatch = useDispatch();
  const addReplyId = useSelector(state => state.comments.add_reply_id);
  const videoPlayer = useSelector(state => state.video_player);
  const { scroll_to_bucket: scrollToComment } = videoPlayer;
  const { record_reply_enabled: recordReplyEnabled } = videoPlayer.video;

  const commentBuckets = useSelector(selectCommentBuckets);

  const scrollTarget = useRef(null);
  const commentContentRef = useRef(null);

  const willHighlight = Object.keys(commentBuckets).length > 1;

  // scroll to selected comment bucket
  useEffect(() => {
    if (!scrollTarget.current || !scrollContainer) {
      return;
    }

    const scrollY =
      scrollTarget.current.getBoundingClientRect().top +
      window.scrollY -
      SCROLL_BUFFER;

    scrollContainer.scrollTop = scrollY;

    if (willHighlight) {
      // we're going to keep scrolling on re-renders unless we clear this out
      scrollTarget.current.addEventListener('animationend', () => {
        dispatch(updateVideoPlayerBucketId(null));
      });
    } else {
      dispatch(updateVideoPlayerBucketId(null));
    }
  }, [scrollTarget, scrollContainer, scrollToComment, willHighlight, dispatch]);

  function isLoomComment(content) {
    return (
      (content.includes(LOOM_DEV_URI) || content.includes(LOOM_URI)) &&
      content.includes('/share/')
    );
  }

  const [renderCommentBucket, setRenderCommentBucket] = useState({});

  // filter out Loom comments if the owner disabled record reply function
  const filteredComments = useMemo(() => {
    const commentsCopy = _cloneDeep(commentBuckets);

    for (const bucketId in commentsCopy) {
      const newComments = [];
      const oldComments = commentsCopy[bucketId].comments;

      oldComments.map(comment => {
        if (!isLoomComment(comment.content) && comment.replies) {
          const replies = comment.replies.filter(
            reply => !isLoomComment(reply.content)
          );

          comment.replies = replies;
          newComments.push(comment);
        }
      });
      commentsCopy[bucketId].comments = newComments;
    }

    return commentsCopy;
  }, [commentBuckets]);

  useEffect(() => {
    if (recordReplyEnabled) {
      setRenderCommentBucket(commentBuckets);
    } else {
      setRenderCommentBucket(filteredComments);
    }
  }, [commentBuckets, recordReplyEnabled, filteredComments]);

  function getAllCommentGroups() {
    return Object.entries(renderCommentBucket).map(([bucketInd, bucket]) => {
      const scrollToThisGroup = scrollToComment == bucketInd;
      const highlightThisGroup = scrollToThisGroup && willHighlight;

      const hightlightStyle = drawnOnSharePage
        ? styles.highlightDark
        : styles.highlight;

      return (
        <div
          ref={ref => {
            if (scrollToThisGroup) {
              scrollTarget.current = ref;
            }
          }}
          className={classNames(styles.commentGroup, {
            [hightlightStyle]: highlightThisGroup,
          })}
          key={bucketInd}
        >
          {bucket.comments.map(comment => {
            return (
              <Comment
                drawnOnSharePage={drawnOnSharePage}
                key={comment.id}
                comment={comment}
                scrollContainer={scrollContainer}
              />
            );
          })}
        </div>
      );
    });
  }

  return (
    <div ref={commentContentRef} className={styles.content}>
      {getAllCommentGroups()}
      {Boolean(!addReplyId) && (
        <>
          <Spacer top="large"></Spacer>
          {!drawnOnSharePage && <AddComment />}
        </>
      )}
    </div>
  );
};

export default CommentsContent;
